import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getLanguages } from "@/api/learner";
import { UI_LANGUAGES } from "@/i18n/languages";
import { presentationFor } from "./framework-config";
import { ContinueChooser } from "./continue-chooser";

const LANGUAGE_TRAINER = "language-trainer";

/** Native name for a language code (e.g. "de" → "Deutsch"), falling back to the
 *  uppercased code when we don't have an endonym for it. */
function nativeNameFor(code: string): string {
  return UI_LANGUAGES.find((l) => l.code === code)?.nativeName ?? code.toUpperCase();
}

/** "Continue learning" — one row per language the learner is mid-course on.
 *  Tapping a row one-tap starts the next adaptive language-trainer set (same
 *  startRun + navigate path the framework gallery uses) and lands on Create,
 *  where the run surfaces in the "Generating now" section. Renders nothing for
 *  new users (empty/loading) so the gallery stays uncluttered. */
export function ContinueLearning() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Which language row is expanded into the steer chooser (null = none).
  const [selected, setSelected] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ["learner-languages"],
    queryFn: getLanguages,
  });

  if (!data || data.length === 0) return null;

  const p = presentationFor(LANGUAGE_TRAINER);

  return (
    <section className="mt-6">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
        {t("Continue learning")}
      </h2>
      <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {data.map((lang) => {
          const on = selected === lang.language_code;
          return (
            <button
              key={lang.language_code}
              type="button"
              onClick={() => setSelected(on ? null : lang.language_code)}
              className={`card flex items-center gap-3 p-3 text-left transition hover:-translate-y-0.5 hover:shadow-glow ${
                on ? "ring-2 ring-brand-400" : ""
              }`}
            >
              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-xl ${p.tile}`}>
                {p.emoji}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink">
                  {nativeNameFor(lang.language_code)} · {lang.cefr_level}
                </p>
                <p className="text-xs font-medium text-brand-600">
                  {on ? t("Pick a direction ↓") : t("Continue →")}
                </p>
              </div>
            </button>
          );
        })}
      </div>
      {selected && (
        <div className="card mt-3 p-4">
          {(() => {
            const lang = data.find((l) => l.language_code === selected)!;
            return (
              <ContinueChooser
                baseInputs={{ target_language: lang.language_code, level: lang.cefr_level }}
                onStarted={() => navigate("/app/create")}
              />
            );
          })()}
        </div>
      )}
    </section>
  );
}
